import { useQuery } from "@tanstack/react-query";
import { api } from "../../shared/api/client";
import type { ReleaseHistoryEntry } from "../../shared/types/api";
import { Card, CardBody, CardHeader, ErrorState, LoadingState, Timeline } from "../../shared/ui";
import { formatTimestamp } from "./releaseVocabulary";

/**
 * §43's activity feed.
 *
 * <p>Phrased as sentences rather than field diffs. "SCOPE_FROZEN" tells a reader nothing they
 * could not see from the badge; "Vijay froze the scope — 64 tickets" is what they came to find
 * out.
 *
 * <p>Some events carry no values on purpose, and those render as the sentence alone. Appending a
 * from/to clause to an entry that never had one is how a timeline ends up claiming a description
 * was set to the word "(updated)".
 */
/** Exported so the printable report says the same thing this timeline does. */
export const PHRASING: Record<string, string> = {
  RELEASE_CREATED: "created this release",
  RELEASE_UPDATED: "edited the release",
  RELEASE_DATE_CHANGED: "moved the release date",
  RELEASE_STATUS_CHANGED: "moved this",
  SCOPE_FROZEN: "froze the scope",
  SCOPE_REOPENED: "reopened the scope",
  WORK_ITEM_ADDED: "added",
  WORK_ITEM_REMOVED: "removed",
  WORK_ITEM_ADDED_AFTER_FREEZE: "added after the freeze",
  WORK_ITEM_REMOVED_AFTER_FREEZE: "removed after the freeze",
  QA_DISPOSITION_CHANGED: "changed whether QA is required",
  QA_STATUS_CHANGED: "recorded a QA result",
  QA_SIGNED_OFF: "signed off QA",
  QA_WAIVER_GRANTED: "waived QA",
  PREREQUISITE_ADDED: "added a prerequisite",
  PREREQUISITE_COMPLETED: "completed a prerequisite",
  PREREQUISITE_FAILED: "recorded a failed prerequisite",
  PREREQUISITE_SKIPPED: "skipped a prerequisite",
  KNOWN_ISSUE_ADDED: "recorded a known issue",
  KNOWN_ISSUE_ACCEPTED: "accepted the risk of a known issue",
  READINESS_EVALUATED: "ran validation",
  READINESS_GATE_OVERRIDDEN: "accepted the risk of a failed gate",
  READINESS_GATE_OVERRIDE_REVOKED: "withdrew a waiver",
  RELEASE_COMPLETED: "completed the release",
  RELEASE_FAILED: "recorded the release as failed",
  RELEASE_ROLLED_BACK: "rolled the release back",
  RELEASE_CANCELLED: "cancelled the release",
};

/** Events recorded as having happened, without the values involved. */
const WITHOUT_VALUES = new Set(["RELEASE_UPDATED", "READINESS_EVALUATED"]);

export function ReleaseActivityTab({ releaseId }: { releaseId: string }) {
  const history = useQuery({
    queryKey: ["release-history", releaseId],
    queryFn: () => api.get<ReleaseHistoryEntry[]>(`/api/v1/releases/${releaseId}/history`),
  });

  if (history.isPending) {
    return <LoadingState label="Loading activity…" />;
  }
  if (history.isError) {
    return <ErrorState error={history.error} onRetry={() => void history.refetch()} />;
  }

  return (
    <Card>
      <CardHeader title="Activity" subtitle="Everything that happened to this release, newest first." />
      <CardBody>
        <Timeline
          entries={history.data.map((entry) => ({
            id: entry.id,
            emphasis:
              entry.eventType === "SCOPE_FROZEN"
              || entry.eventType === "READINESS_GATE_OVERRIDDEN"
              || entry.eventType === "RELEASE_STATUS_CHANGED",
            title: (
              <>
                <span className="font-medium text-ink-900">
                  {entry.actorName ?? "The system"}
                </span>{" "}
                {PHRASING[entry.eventType] ?? "changed something"}
                {entry.transitionLabel && (
                  <>
                    {" "}
                    <span className="rounded-md bg-brand-50 px-1.5 py-0.5 text-xs font-medium text-brand-700">
                      {entry.transitionLabel}
                    </span>
                  </>
                )}
                {entry.workItemKey && (
                  <>
                    {" "}
                    <span className="font-mono text-xs text-ink-700">{entry.workItemKey}</span>
                  </>
                )}
                {!WITHOUT_VALUES.has(entry.eventType)
                  && entry.oldValue != null
                  && entry.newValue != null && (
                  <>
                    {" "}
                    <span className="text-ink-500">from</span>{" "}
                    <span className="font-medium text-ink-700">{entry.oldValue}</span>{" "}
                    <span className="text-ink-500">to</span>{" "}
                    <span className="font-medium text-ink-900">{entry.newValue}</span>
                  </>
                )}
                {!WITHOUT_VALUES.has(entry.eventType)
                  && entry.oldValue == null
                  && entry.newValue != null
                  && !entry.workItemKey && (
                  <>
                    {" "}
                    <span className="font-medium text-ink-900">{entry.newValue}</span>
                  </>
                )}
              </>
            ),
            detail: entry.reasonComment ?? undefined,
            meta: formatTimestamp(entry.eventTimestamp),
          }))}
        />
      </CardBody>
    </Card>
  );
}
